import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, DetectedYoga, YogaCategory } from '../types/kundali';
import { 
  ShieldAlert, 
  Flame, 
  Moon, 
  Sun, 
  Sparkles, 
  Award, 
  Star, 
  BookOpen, 
  CheckCircle2, 
  AlertTriangle,
  Search,
  Crown
} from 'lucide-react';

interface DoshaSectionProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const DoshaSection: React.FC<DoshaSectionProps> = ({ kundali, lang = 'en' }) => {
  const { manglik, kaalSarp, sadeSati, pitraDosha } = kundali;
  const yogas: DetectedYoga[] = kundali.yogas || [];

  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const highPotencyCount = useMemo(() => yogas.filter(y => y.fruitionStatus === 'HIGH_POTENCY').length, [yogas]);
  const moderatePotencyCount = useMemo(() => yogas.filter(y => y.fruitionStatus === 'MODERATE_POTENCY').length, [yogas]);
  const dormantCount = useMemo(() => yogas.filter(y => y.fruitionStatus === 'AFFLICTED_OR_DORMANT').length, [yogas]);

  const categories = useMemo(() => [
    { id: 'ALL', label: lang === 'hi' ? 'सभी योग' : 'All Yogas', count: yogas.length },
    { id: 'RAJA', label: lang === 'hi' ? 'राज योग' : 'Raja Yogas', count: yogas.filter(y => y.category === 'RAJA').length },
    { id: 'DHANA', label: lang === 'hi' ? 'धन योग' : 'Dhana Yogas', count: yogas.filter(y => y.category === 'DHANA').length },
    { id: 'MAHAPURUSHA', label: lang === 'hi' ? 'पंच महापुरुष' : 'Mahapurusha', count: yogas.filter(y => y.category === 'MAHAPURUSHA').length },
    { id: 'SOLAR_LUNAR', label: lang === 'hi' ? 'सूर्य-चंद्र योग' : 'Solar & Lunar', count: yogas.filter(y => y.category === 'SOLAR_LUNAR').length },
    { id: 'NABHASA', label: lang === 'hi' ? 'नाभस योग' : 'Nabhasa Yogas', count: yogas.filter(y => y.category === 'NABHASA').length },
    { id: 'CLASSICAL', label: lang === 'hi' ? 'शास्त्रीय / अन्य' : 'Classical / Other', count: yogas.filter(y => y.category === 'CLASSICAL' || y.category === 'MISCELLANEOUS').length },
  ], [yogas, lang]);

  const filteredYogas = useMemo(() => {
    return yogas.filter(y => {
      const matchesCategory = selectedCategory === 'ALL'
        ? true
        : selectedCategory === 'CLASSICAL'
          ? (y.category === 'CLASSICAL' || y.category === 'MISCELLANEOUS')
          : y.category === selectedCategory;

      if (!matchesCategory) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const nameMatch = y.name.toLowerCase().includes(q) || (y.sanskritName && y.sanskritName.toLowerCase().includes(q));
      const planetMatch = y.participatingPlanets?.some(p => p.toLowerCase().includes(q));
      return nameMatch || planetMatch;
    });
  }, [yogas, selectedCategory, searchQuery]);

  const getCategoryBadgeClass = (category: YogaCategory) => {
    switch (category) {
      case 'RAJA':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'DHANA':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'MAHAPURUSHA':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'SOLAR_LUNAR':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'NABHASA':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-astro-primary/10 text-astro-primary border-astro-primary/20';
    }
  };

  const getFruitionBadge = (status: string) => {
    switch (status) {
      case 'HIGH_POTENCY':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
            {lang === 'hi' ? 'पूर्ण फल (High Potency)' : 'High Potency'}
          </span>
        );
      case 'MODERATE_POTENCY':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
            {lang === 'hi' ? 'मध्यम फल (Moderate)' : 'Moderate Potency'}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-slate-100 text-slate-700 border-slate-300 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
            {lang === 'hi' ? 'सुषुप्त फल (Dormant)' : 'Dormant / Afflicted'}
          </span>
        );
    }
  };

  const getCategoryIcon = (category: YogaCategory) => {
    switch (category) {
      case 'RAJA':
        return <Crown className="w-4 h-4 text-purple-600" />;
      case 'DHANA':
        return <Sparkles className="w-4 h-4 text-emerald-600" />;
      case 'MAHAPURUSHA':
        return <Star className="w-4 h-4 text-amber-600" />;
      case 'SOLAR_LUNAR':
        return <Sun className="w-4 h-4 text-blue-600" />;
      default:
        return <Award className="w-4 h-4 text-astro-primary" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* ------------------------------------------------------------------- */}
      {/* DOSHA ANALYSIS SECTION                                              */}
      {/* ------------------------------------------------------------------- */}
      <div className="space-y-6">
        <div className="border-b border-astro-border pb-2">
          <h2 className="text-lg font-serif font-bold text-astro-primary flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-astro-primary" />
            {lang === 'hi' ? 'दोष एवं पीड़ा विश्लेषण' : 'Dosha & Affliction Analysis'}
          </h2>
          <p className="text-xs text-astro-secondary-text">
            {lang === 'hi' 
              ? 'मंगल दोष, कालसर्प योग, साढ़े साती एवं पितृ दोष की शास्त्रीय स्थिति तथा वैदिक उपाय।'
              : 'Classical Vedic assessment of Manglik, Kaal Sarp, Sade Sati, and Pitra Dosha with remedial measures.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Manglik Dosha Card */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Flame className={`w-5 h-5 ${manglik.isManglik ? 'text-rose-500' : 'text-astro-primary'}`} />
                  <h3 className="font-bold text-astro-primary font-serif text-base">Manglik (Kuja) Dosha</h3>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${
                    manglik.isManglik
                      ? 'bg-rose-100 text-rose-700 border-rose-300'
                      : 'bg-astro-primary/10 text-astro-primary border-astro-primary/30'
                  }`}
                >
                  {manglik.isManglik ? 'Dosha Present' : 'No Manglik Dosha'}
                </span>
              </div>

              <p className="text-xs text-astro-text leading-relaxed mb-3">{manglik.summary}</p>

              {manglik.isManglik && (
                <div className="space-y-3 text-xs mb-4">
                  {/* Severity Meter */}
                  <div>
                    <div className="flex justify-between text-[11px] text-astro-secondary-text mb-1">
                      <span>Severity Level:</span>
                      <span className="font-bold text-rose-600">{manglik.severityPercent}%</span>
                    </div>
                    <div className="w-full bg-astro-card h-2 rounded-full overflow-hidden border border-astro-border">
                      <div
                        className="bg-gradient-to-r from-astro-accent via-orange-500 to-rose-500 h-full transition-all duration-500"
                        style={{ width: `${manglik.severityPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Placements */}
                  {manglik.placements.length > 0 && (
                    <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                      <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase mb-1">Mars Placements:</span>
                      <ul className="list-disc list-inside text-astro-text space-y-0.5">
                        {manglik.placements.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cancellations */}
                  {manglik.cancellations.length > 0 && (
                    <div className="p-2.5 bg-astro-primary/10 rounded-xl border border-astro-primary/20 text-astro-primary">
                      <span className="text-[10px] block font-bold uppercase mb-1">Cancellation Factors:</span>
                      <ul className="list-disc list-inside space-y-0.5">
                        {manglik.cancellations.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Remedies */}
            <div className="pt-3 border-t border-astro-border">
              <h4 className="text-[11px] font-bold text-astro-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Vedic Remedies
              </h4>
              <ul className="text-xs text-astro-text space-y-1">
                {manglik.remedies.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-astro-primary mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Kaal Sarp Dosha Card */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <ShieldAlert className={`w-5 h-5 ${kaalSarp.hasKaalSarp ? 'text-astro-primary' : 'text-[#6F9690]'}`} />
                  <h3 className="font-bold text-astro-primary font-serif text-base">Kaal Sarp Yoga</h3>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${
                    kaalSarp.hasKaalSarp
                      ? 'bg-astro-primary/15 text-astro-primary border-astro-primary/30'
                      : 'bg-[#6F9690]/15 text-astro-primary border-[#6F9690]/30'
                  }`}
                >
                  {kaalSarp.hasKaalSarp ? kaalSarp.type || 'Present' : 'Absent'}
                </span>
              </div>

              <p className="text-xs text-astro-text leading-relaxed mb-4">{kaalSarp.description}</p>
            </div>

            <div className="pt-3 border-t border-astro-border">
              <h4 className="text-[11px] font-bold text-astro-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Remedial Actions
              </h4>
              <ul className="text-xs text-astro-text space-y-1">
                {kaalSarp.remedies.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-astro-primary mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sade Sati Card */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Moon className={`w-5 h-5 ${sadeSati.isInSadeSati ? 'text-astro-primary' : 'text-[#6F9690]'}`} />
                  <h3 className="font-bold text-astro-primary font-serif text-base">Saturn Sade Sati Status</h3>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${
                    sadeSati.isInSadeSati
                      ? 'bg-astro-primary/15 text-astro-primary border-astro-primary/30'
                      : 'bg-[#6F9690]/15 text-astro-primary border-[#6F9690]/30'
                  }`}
                >
                  {sadeSati.phase}
                </span>
              </div>

              <p className="text-xs text-astro-text leading-relaxed mb-3">{sadeSati.description}</p>

              {sadeSati.activePeriodDetails && (
                <div className="mb-3 p-2.5 bg-astro-secondary-surface rounded-xl border border-[#E2DED5] text-xs">
                  <div className="text-[10px] font-bold text-astro-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-astro-secondary-surface0 animate-pulse" />
                    Active Period (Day / Month / Year)
                  </div>
                  <div className="font-mono font-bold text-[#263633]">
                    {sadeSati.activePeriodDetails.formattedStartDate} to {sadeSati.activePeriodDetails.formattedEndDate} ({sadeSati.activePeriodDetails.durationFormatted})
                  </div>
                  <div className="text-[11px] text-astro-secondary mt-1">
                    Coinciding Dasha: <strong>{sadeSati.activePeriodDetails.coincidingDasha || 'Active Sequence'}</strong>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] block font-semibold uppercase">Natal Moon Sign</span>
                  <span className="font-semibold text-astro-primary">{sadeSati.moonSign}</span>
                </div>
                <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] block font-semibold uppercase">Transit Saturn Sign</span>
                  <span className="font-semibold text-astro-text">{sadeSati.saturnSign}</span>
                </div>
              </div>

              {sadeSati.timeline && sadeSati.timeline.length > 0 && (
                <div className="mb-3 p-2.5 bg-astro-card rounded-xl border border-astro-border text-xs">
                  <span className="text-astro-secondary-text text-[10px] block font-semibold uppercase mb-1.5">
                    Lifetime Shani Cycles ({sadeSati.timeline.length} Ingress Periods)
                  </span>
                  <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                    {sadeSati.timeline.slice(0, 6).map((item) => (
                      <div
                        key={item.id}
                        className={`p-1.5 rounded-lg flex items-center justify-between text-[11px] border ${
                          item.status === 'Active'
                            ? 'bg-astro-secondary/15 border-[#E2DED5] text-[#263633] font-bold'
                            : 'bg-white/60 border-astro-border text-astro-text'
                        }`}
                      >
                        <span className="truncate max-w-[160px]">{item.phaseLabel}</span>
                        <span className="font-mono text-[10px] text-astro-primary font-semibold">
                          {item.formattedStartDate} — {item.formattedEndDate}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-astro-border">
              <h4 className="text-[11px] font-bold text-astro-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Shani Shanti Upayas
              </h4>
              <ul className="text-xs text-astro-text space-y-1">
                {sadeSati.remedies.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-astro-primary mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pitra Dosha Card */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Sun className={`w-5 h-5 ${pitraDosha.hasPitraDosha ? 'text-astro-primary' : 'text-[#6F9690]'}`} />
                  <h3 className="font-bold text-astro-primary font-serif text-base">Pitra (Ancestral) Dosha</h3>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${
                    pitraDosha.hasPitraDosha
                      ? 'bg-astro-primary/15 text-astro-primary border-astro-primary/30'
                      : 'bg-[#6F9690]/15 text-astro-primary border-[#6F9690]/30'
                  }`}
                >
                  {pitraDosha.hasPitraDosha ? `${pitraDosha.severity} Severity` : 'No Pitra Dosha'}
                </span>
              </div>

              {pitraDosha.hasPitraDosha ? (
                <div className="space-y-2 mb-3 text-xs">
                  <span className="text-[10px] text-astro-secondary-text font-semibold uppercase block">Contributing Factors:</span>
                  <ul className="list-disc list-inside text-astro-text space-y-1">
                    {pitraDosha.reasons.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-astro-text leading-relaxed mb-4">
                  Sun and the 9th house of ancestors are free from severe Rahu/Ketu afflictions.
                </p>
              )}
            </div>

            <div className="pt-3 border-t border-astro-border">
              <h4 className="text-[11px] font-bold text-astro-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Pitru Tarpan & Remedies
              </h4>
              <ul className="text-xs text-astro-text space-y-1">
                {pitraDosha.remedies.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-astro-primary mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* VEDIC YOGAS SECTION (FEAT-YOGA-001)                                 */}
      {/* ------------------------------------------------------------------- */}
      <div className="space-y-6 pt-4 border-t border-astro-border">
        {/* Header & Overview */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-serif font-bold text-astro-primary flex items-center gap-2">
              <Award className="w-6 h-6 text-astro-primary" />
              {lang === 'hi' ? 'वैदिक शुभ एवं विशेष योग' : 'Detected Vedic Yogas & Combinations'}
            </h2>
            <p className="text-xs text-astro-secondary-text mt-1 max-w-2xl leading-relaxed">
              {lang === 'hi'
                ? 'महर्षि पराशर प्रतिपादित राज योग, धन योग, पंच महापुरुष, नाभस एवं सूर्य-चंद्र योग। अष्टकवर्ग (SAV ≥ 28) और षड्बल (अनुपात ≥ 1.0) से फलित क्षमता प्रमाणित।'
                : 'Classical Parashari Raja, Dhana, Pancha Mahapurusha, Nabhasa, and Solar/Lunar yogas evaluated from natal geometry. Fruition potency modulated by Layer 6 Ashtakavarga and Layer 7 Shadbala.'}
            </p>
          </div>

          {/* Potency Summary Chips */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="px-3 py-1.5 bg-astro-card border border-astro-border rounded-xl text-xs flex items-center gap-1.5 shadow-sm">
              <span className="text-[10px] text-astro-secondary-text uppercase font-semibold">Total:</span>
              <span className="font-bold text-astro-primary font-mono">{yogas.length}</span>
            </div>
            <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs flex items-center gap-1.5 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] text-emerald-800 uppercase font-semibold">High:</span>
              <span className="font-bold text-emerald-800 font-mono">{highPotencyCount}</span>
            </div>
            <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl text-xs flex items-center gap-1.5 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-[10px] text-amber-800 uppercase font-semibold">Moderate:</span>
              <span className="font-bold text-amber-800 font-mono">{moderatePotencyCount}</span>
            </div>
            {dormantCount > 0 && (
              <div className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs flex items-center gap-1.5 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-slate-400" />
                <span className="text-[10px] text-slate-700 uppercase font-semibold">Dormant:</span>
                <span className="font-bold text-slate-700 font-mono">{dormantCount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Filter Controls: Category Tabs & Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-astro-card/60 p-2.5 rounded-2xl border border-astro-border">
          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 flex items-center gap-1.5 border ${
                  selectedCategory === cat.id
                    ? 'bg-astro-primary text-white border-astro-primary shadow-sm'
                    : 'bg-astro-bg text-astro-secondary-text border-astro-border hover:text-astro-primary hover:border-astro-primary/40'
                }`}
              >
                <span>{cat.label}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  selectedCategory === cat.id ? 'bg-white/25 text-white' : 'bg-astro-card text-astro-secondary-text'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* Quick Search */}
          <div className="relative min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-astro-secondary-text" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={lang === 'hi' ? 'योग या ग्रह खोजें...' : 'Search yoga or planet...'}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-astro-bg border border-astro-border rounded-xl focus:outline-none focus:border-astro-primary text-astro-text placeholder:text-astro-secondary-text/60"
            />
          </div>
        </div>

        {/* Yogas Grid */}
        {filteredYogas.length === 0 ? (
          <div className="p-8 text-center bg-astro-card/40 rounded-2xl border border-astro-border text-astro-secondary-text">
            <Award className="w-8 h-8 mx-auto text-astro-secondary-text/50 mb-2" />
            <p className="text-sm font-semibold">
              {lang === 'hi' ? 'कोई योग नहीं मिला।' : 'No yogas found matching the selected filter.'}
            </p>
            <p className="text-xs text-astro-secondary-text/80 mt-1">
              {lang === 'hi' ? 'कृपया अन्य श्रेणी या खोज शब्द चुनें।' : 'Try selecting another category or clearing search query.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredYogas.map((yoga) => {
              const desc = yoga.description?.[lang] || yoga.description?.en || '';
              const res = yoga.result?.[lang] || yoga.result?.en || '';

              return (
                <div
                  key={yoga.id}
                  className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col justify-between hover:border-astro-primary/40 transition-colors duration-200"
                >
                  <div>
                    {/* Header: Title, Category & Fruition Badge */}
                    <div className="flex items-start justify-between gap-3 mb-3 border-b border-astro-border pb-3">
                      <div className="flex items-start gap-2.5">
                        <div className="p-2 rounded-xl bg-astro-card border border-astro-border mt-0.5">
                          {getCategoryIcon(yoga.category)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-astro-primary font-serif text-base leading-snug">
                              {yoga.name}
                            </h3>
                            {yoga.sanskritName && (
                              <span className="text-xs font-serif text-astro-secondary-text font-normal">
                                ({yoga.sanskritName})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getCategoryBadgeClass(yoga.category)}`}>
                              {yoga.category}
                            </span>
                            {yoga.classicalSource && (
                              <span className="text-[10px] text-astro-secondary-text flex items-center gap-1">
                                <BookOpen className="w-3 h-3" />
                                {yoga.classicalSource}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        {getFruitionBadge(yoga.fruitionStatus)}
                      </div>
                    </div>

                    {/* Description & Predicted Result */}
                    <p className="text-xs text-astro-text leading-relaxed mb-3">
                      {desc}
                    </p>

                    {res && (
                      <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border mb-3 text-xs">
                        <span className="text-[10px] text-astro-primary font-bold uppercase tracking-wider block mb-1">
                          {lang === 'hi' ? 'फलश्रुति (Classical Result):' : 'Classical Result / Phala:'}
                        </span>
                        <p className="text-astro-text leading-relaxed">
                          {res}
                        </p>
                      </div>
                    )}

                    {/* Technical Participating Factors */}
                    <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
                      <div className="p-2 bg-astro-card rounded-xl border border-astro-border">
                        <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase">
                          {lang === 'hi' ? 'सहभागी ग्रह' : 'Planets'}:
                        </span>
                        <span className="font-semibold text-astro-primary truncate block">
                          {yoga.participatingPlanets?.length > 0 ? yoga.participatingPlanets.join(', ') : '—'}
                        </span>
                      </div>
                      <div className="p-2 bg-astro-card rounded-xl border border-astro-border">
                        <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase">
                          {lang === 'hi' ? 'सहभागी भाव' : 'Houses'}:
                        </span>
                        <span className="font-semibold text-astro-text truncate block">
                          {yoga.participatingHouses?.length > 0 ? yoga.participatingHouses.map(h => `H${h}`).join(', ') : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Qualifying Factors */}
                    {yoga.qualifyingFactors && yoga.qualifyingFactors.length > 0 && (
                      <div className="mb-3 p-2 bg-emerald-50/70 rounded-xl border border-emerald-200/60 text-xs text-emerald-950">
                        <span className="text-[10px] block font-bold uppercase tracking-wider text-emerald-800 mb-1 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          {lang === 'hi' ? 'प्रबलता कारक' : 'Fruition Strengths'}:
                        </span>
                        <ul className="list-disc list-inside space-y-0.5 text-[11px] text-emerald-900">
                          {yoga.qualifyingFactors.map((q, idx) => (
                            <li key={idx}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Afflictions or Cancellations */}
                    {yoga.cancellationOrAfflictionFactors && yoga.cancellationOrAfflictionFactors.length > 0 && (
                      <div className="mb-3 p-2 bg-amber-50/70 rounded-xl border border-amber-200/60 text-xs text-amber-950">
                        <span className="text-[10px] block font-bold uppercase tracking-wider text-amber-800 mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-amber-600" />
                          {lang === 'hi' ? 'बाधक / न्यूनता कारक' : 'Afflictions / Inhibitors'}:
                        </span>
                        <ul className="list-disc list-inside space-y-0.5 text-[11px] text-amber-900">
                          {yoga.cancellationOrAfflictionFactors.map((a, idx) => (
                            <li key={idx}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Potency Strength Meter Footer */}
                  <div className="pt-3 border-t border-astro-border mt-1">
                    <div className="flex justify-between items-center text-[11px] text-astro-secondary-text mb-1">
                      <span className="font-semibold">
                        {lang === 'hi' ? 'योग शक्ति स्कोर' : 'Potency Score'}:
                      </span>
                      <span className="font-bold font-mono text-astro-primary">
                        {yoga.strengthScore}%
                      </span>
                    </div>
                    <div className="w-full bg-astro-card h-1.5 rounded-full overflow-hidden border border-astro-border">
                      <div
                        className={`h-full transition-all duration-500 ${
                          yoga.fruitionStatus === 'HIGH_POTENCY'
                            ? 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                            : yoga.fruitionStatus === 'MODERATE_POTENCY'
                              ? 'bg-gradient-to-r from-amber-400 to-amber-600'
                              : 'bg-gradient-to-r from-slate-300 to-slate-500'
                        }`}
                        style={{ width: `${yoga.strengthScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
